import { useEffect, useRef, useState } from 'preact/hooks';
import { RadioPlayer } from './RadioPlayer.js';
import { t } from '../i18n/index.js';
import { audioStatus, getSoundSettings, onSoundSettings, playSound, setSoundSettings, unlockAudio } from '../audio/sounds.js';
import { onRadio, radioConfig } from '../audio/radio.js';

/** Sound settings (suunnitelma.md section 32). Stored per browser. */
export function SoundSettingsCard() {
  const [s, setS] = useState(getSoundSettings());
  useEffect(() => onSoundSettings(setS), []);
  const [status, setStatus] = useState(audioStatus());
  useEffect(() => {
    const timer = setInterval(() => setStatus(audioStatus()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section class="card form">
      <h2>{t.sound.title}</h2>
      <label class="switch">
        <input type="checkbox" checked={s.enabled} onChange={(e) => setSoundSettings({ ...s, enabled: e.currentTarget.checked })} />
        <span class="switch-track" aria-hidden="true" />
        {t.sound.enabled}
      </label>
      <label>
        {t.sound.volume}: {Math.round(s.volume * 100)} %
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(s.volume * 100)}
          disabled={!s.enabled}
          onInput={(e) => setSoundSettings({ ...s, volume: Number(e.currentTarget.value) / 100 })}
        />
      </label>
      <div>
        <button
          class="btn btn-small"
          disabled={!s.enabled}
          onClick={() => {
            unlockAudio();
            playSound('hit');
            setTimeout(() => setStatus(audioStatus()), 300);
          }}
        >
          {t.sound.test}
        </button>
      </div>
      <label class="switch">
        <input type="checkbox" checked={s.chatSounds} onChange={(e) => setSoundSettings({ ...s, chatSounds: e.currentTarget.checked })} />
        <span class="switch-track" aria-hidden="true" />
        {t.sound.chatSounds}
      </label>
      <label class="switch">
        <input
          type="checkbox"
          checked={s.pmNotifications}
          onChange={(e) => setSoundSettings({ ...s, pmNotifications: e.currentTarget.checked })}
        />
        <span class="switch-track" aria-hidden="true" />
        {t.sound.pmNotifications}
      </label>
      <p class="hint">{t.sound.pmNotificationsHint}</p>
      <p class="hint">{t.sound.info}</p>
      <p class="hint">
        {t.sound.status}: {t.sound.states[status.context]} · {t.sound.session}: {status.session}
      </p>
    </section>
  );
}

/** Radio toggle for the top bar; only for players with a music permission (decision 2026-09-29). */
export function MusicButton() {
  const [s, setS] = useState(getSoundSettings());
  useEffect(() => onSoundSettings(setS), []);
  const [config, setConfig] = useState(radioConfig());
  useEffect(() => onRadio(() => setConfig(radioConfig())), []);
  // The button opens the radio controls (volume also during a game, user request 2026-09-29).
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    addEventListener('pointerdown', onDown);
    addEventListener('keydown', onKey);
    return () => {
      removeEventListener('pointerdown', onDown);
      removeEventListener('keydown', onKey);
    };
  }, [open]);
  if (!config) return null;
  const silent = !s.music || s.musicMuted;
  return (
    <span class="radio-menu" ref={ref}>
      <button
        class={`btn btn-ghost btn-icon ${silent ? 'off' : ''}`}
        onClick={() => setOpen(!open)}
        title={t.radio.menu(config.name)}
        aria-label={t.radio.menu(config.name)}
        aria-expanded={open}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <path d="M9 17V5l11-2v12" />
          <circle cx="6.5" cy="17.5" r="2.5" fill="currentColor" stroke="none" />
          <circle cx="17.5" cy="15.5" r="2.5" fill="currentColor" stroke="none" />
          {silent && <path d="M3 3l18 18" />}
        </svg>
      </button>
      {open && (
        <div class="radio-popover" role="dialog" aria-label={config.name}>
          <RadioPlayer compact />
        </div>
      )}
    </span>
  );
}

/** Quick mute toggle for the top bar. */
export function MuteButton() {
  const [s, setS] = useState(getSoundSettings());
  useEffect(() => onSoundSettings(setS), []);
  const label = s.enabled ? t.sound.mute : t.sound.unmute;
  return (
    <button class="btn btn-ghost btn-icon" onClick={() => setSoundSettings({ ...s, enabled: !s.enabled })} title={label} aria-label={label}>
      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
        <path d="M4 9h4l5-4v14l-5-4H4z" fill="currentColor" stroke="none" />
        {s.enabled ? (
          <>
            <path d="M16.5 8.5a5 5 0 0 1 0 7" />
            <path d="M19 6a8.5 8.5 0 0 1 0 12" />
          </>
        ) : (
          <path d="M17 9l5 6M22 9l-5 6" />
        )}
      </svg>
    </button>
  );
}
